/**
 * Endpoints list state: fetched from `GET /v1/family/endpoints` on demand and
 * mirrored into a signal so the dashboard route can render reactively.
 *
 * The Rust `endpoints` table is authoritative; this hook holds a client-side
 * cache that is intentionally simple — one-shot refresh, no diffing. The
 * dashboard route calls `refresh()` on mount and (later) on a poll timer.
 */

import { signal } from '@preact/signals';
import { api, apiDelete, apiPatchJson, apiPostBytes } from '../state/api';
import { familyKeyInMemory } from '../state/session';
import { wrapFamilyKeyForEndpoint } from '../e2ee/wrap';
import { currentConfig } from './config';
import { syncDeviceConfigs } from './deviceConfig';

/**
 * True when the last refresh saw endpoints that need a family-key wrap but we
 * couldn't produce one because the family key isn't unlocked. The dashboard
 * reads this to show a "Unlock encryption to finish enrolling" banner instead
 * of silently leaving the endpoint stuck.
 */
export const needsFamilyKeyUnlock = signal(false);

export interface Endpoint {
  id: string;
  label: string | null;
  platform: string;
  agent_version: string;
  last_heartbeat_at: string | null;
  last_screenshot_at: string | null;
  status: string;
  /** age recipient (bech32 `age1…`) used to wrap the family key for this endpoint. */
  x25519_pub: string;
  /** True once the parent PWA has uploaded a family-key wrap for this endpoint. */
  has_wrap: boolean;
  /**
   * `endpoint_configs.version_number`, or null when no per-device document
   * has been written yet. Null drives the config-on-sight loop in
   * `refresh()`.
   */
  config_version: number | null;
  /**
   * What this endpoint last reported applying, via heartbeat. Compare with
   * `config_version` to answer "is this device running what I last saved?".
   */
  config_version_applied: number | null;
}

/**
 * First endpoint release that reads its own per-device config document.
 * Older binaries are still on the family route, so an override saved for
 * one of them is stored but not applied — the UI has to say so rather than
 * showing a tick. Mirrors `DEVICE_CONFIG_MIN_VERSION` in
 * `crates/control-plane/src/routes/endpoints.rs`.
 */
export const DEVICE_CONFIG_MIN_VERSION = '0.0.64';

/** Whether this endpoint's build applies per-device config. */
export function supportsDeviceConfig(agentVersion: string): boolean {
  const parse = (v: string): [number, number, number] | null => {
    const parts = v.split('.');
    if (parts.length < 3) return null;
    const nums = parts.slice(0, 3).map((p) => {
      const digits = /^\d+/.exec(p);
      return digits === null ? NaN : Number(digits[0]);
    });
    if (nums.some(Number.isNaN)) return null;
    return [nums[0] ?? 0, nums[1] ?? 0, nums[2] ?? 0];
  };
  const got = parse(agentVersion);
  const min = parse(DEVICE_CONFIG_MIN_VERSION);
  if (got === null || min === null) return false;
  for (let i = 0; i < 3; i += 1) {
    const g = got[i] ?? 0;
    const m = min[i] ?? 0;
    if (g !== m) return g > m;
  }
  return true;
}

/**
 * Devices whose per-device write failed on the last refresh or save. The
 * dashboard surfaces these rather than letting a partial fan-out look like
 * a clean save.
 */
export const deviceConfigWriteErrors = signal<string[]>([]);

export const endpoints = signal<Endpoint[]>([]);

/** One scheduler tick's outcome from `endpoint_activity`. */
export interface ActivityEntry {
  at: string;
  flagged: boolean;
  category: string | null;
}

/**
 * Per-endpoint activity ledger. Populated by `refreshActivity()` which
 * fetches the last N ticks for each endpoint. Empty inner list means
 * "endpoint enrolled but hasn't captured yet."
 */
export const activityByEndpoint = signal<Record<string, ActivityEntry[]>>({});

/** Pull the last `limit` activity rows for a single endpoint. */
export async function fetchActivity(
  endpointId: string,
  limit = 10,
): Promise<ActivityEntry[]> {
  return api<ActivityEntry[]>(
    `/v1/family/endpoints/${endpointId}/activity?limit=${String(limit)}`,
  );
}

/** Refresh activity for every currently-known endpoint in parallel. */
export async function refreshActivity(limit = 10): Promise<void> {
  const ids = endpoints.value.map((e) => e.id);
  const results = await Promise.all(
    ids.map(async (id) => {
      try {
        const rows = await fetchActivity(id, limit);
        return [id, rows] as const;
      } catch {
        return [id, [] as ActivityEntry[]] as const;
      }
    }),
  );
  const next: Record<string, ActivityEntry[]> = {};
  for (const [id, rows] of results) next[id] = rows;
  activityByEndpoint.value = next;
}

/**
 * Fetch the endpoints list; then deliver anything a device is missing:
 *
 *  1. **Wrap on sight** — an endpoint with no family-key wrap gets one,
 *     encrypted under its X25519 recipient.
 *  2. **Config on sight** — an endpoint with no per-device config document
 *     gets one, resolved from the family defaults plus its own overrides.
 *
 * The second rides on the first for a reason. A newly-enrolled device
 * already cannot use any config until the parent opens this app with the
 * family key unlocked, because that is when its wrap is delivered. So
 * putting the PWA in charge of writing config documents adds no new
 * blocking dependency — it is the same loop, doing one more thing.
 *
 * Both steps are best-effort: a failure logs and the next tick retries.
 */
export async function refresh(): Promise<void> {
  // `api()` returns null for a 204 or an empty body, and its signature
  // can't express that — so an empty response would put `null` into a
  // signal typed `Endpoint[]`. Every consumer calls `.filter()` or `.map()`
  // on it, so that lands as a page-level crash rather than an empty list.
  const raw = await api<Endpoint[]>('/v1/family/endpoints');
  const list: Endpoint[] = Array.isArray(raw) ? raw : [];
  endpoints.value = list;

  // Pull activity in the background — non-blocking so a slow activity fetch
  // doesn't hold up the wrap flow or the dashboard render.
  void refreshActivity();

  const pending = list.filter((e) => !e.has_wrap);
  const fk = familyKeyInMemory.value;
  // Surface the "locked family key + unwrapped endpoint" case so the
  // dashboard can prompt the user to unlock. Silently short-circuiting is
  // what left previous enrollments stuck.
  needsFamilyKeyUnlock.value = fk === null && pending.length > 0;
  if (fk === null) return;

  if (pending.length > 0) {
    await Promise.all(
      pending.map(async (e) => {
        try {
          const wrapped = await wrapFamilyKeyForEndpoint(fk, e.x25519_pub);
          await apiPostBytes(
            `/v1/family/endpoints/${e.id}/family-key-wrap`,
            wrapped,
          );
        } catch (err) {
          // Non-fatal: log and let the next tick retry. Common causes: server
          // hiccup, endpoint transiently missing an entry, malformed pubkey.
          console.warn(`wrap for endpoint ${e.id} failed`, err);
        }
      }),
    );
    // Optimistically flip has_wrap so we don't try again mid-tick.
    endpoints.value = list.map((e) =>
      pending.some((p) => p.id === e.id) ? { ...e, has_wrap: true } : e,
    );
  }

  await writeMissingDeviceConfigs(list);
}

/**
 * Config-on-sight: write a document for any endpoint that has none.
 *
 * Needs the family config loaded, which the dashboard seeds on mount. If it
 * isn't loaded yet we skip quietly — the next poll tick catches it, and
 * writing a `DEFAULT_CONFIG`-derived document over the parent's real
 * settings would be far worse than waiting.
 */
async function writeMissingDeviceConfigs(list: Endpoint[]): Promise<void> {
  if (currentConfig.value === null) return;
  const missing = list
    .filter((e) => e.config_version === null && e.status !== 'revoked')
    .map((e) => e.id);
  if (missing.length === 0) return;

  try {
    const results = await syncDeviceConfigs(missing);
    const failed = results.filter((r) => !r.ok);
    deviceConfigWriteErrors.value = failed.map((r) => r.endpointId);
    for (const f of failed) {
      console.warn(`device config write for ${f.endpointId} failed`, f.error);
    }
    // Optimistically mark the successes so a fast second poll doesn't
    // re-issue the same writes before the server round-trips.
    const wrote = new Set(results.filter((r) => r.ok).map((r) => r.endpointId));
    endpoints.value = endpoints.value.map((e) =>
      wrote.has(e.id) ? { ...e, config_version: 1 } : e,
    );
  } catch (err) {
    console.warn('config-on-sight failed', err);
  }
}

/** DELETE an endpoint, then refresh the list. */
export async function removeEndpoint(id: string): Promise<void> {
  await apiDelete(`/v1/family/endpoints/${id}`);
  await refresh();
}

/**
 * PATCH an endpoint's label. Optimistically updates the local signal so the
 * dashboard row flips to the new name immediately; reverts on error.
 */
export async function renameEndpoint(id: string, label: string): Promise<void> {
  const trimmed = label.trim().slice(0, 80);
  const previous = endpoints.value;
  endpoints.value = previous.map((e) =>
    e.id === id ? { ...e, label: trimmed.length === 0 ? null : trimmed } : e,
  );
  try {
    await apiPatchJson(`/v1/family/endpoints/${id}`, { label: trimmed });
  } catch (err) {
    endpoints.value = previous;
    throw err;
  }
}
